// maths.portll.net — parent session context.
//
// Adapted DOWN from antidote-dev AdminAuthContext: we keep the single auth listener
// and the intentional-logout guard, and DROP bridge tokens / MFA / the dual-storage
// role split (this is a family-device app; persistence is the feature). betaAccess is
// read from the trusted access plane (client can read, never forge).

import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { onAuthChange, ensureParentDoc, getAccess, signOutUser } from "../lib/firebase.js";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [betaAccess, setBetaAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const intentionalLogout = useRef(false);

  useEffect(() => {
    const unsub = onAuthChange(async (u) => {
      if (u) {
        try {
          await ensureParentDoc(u);
          const access = await getAccess(u.uid);
          setBetaAccess(!!access.betaAccess);
        } catch {
          setBetaAccess(false);
        }
        setUser(u);
      } else {
        setUser(null);
        setBetaAccess(false);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  async function logout() {
    intentionalLogout.current = true;
    await signOutUser();
  }

  /** Re-read betaAccess after redeeming an invite. */
  async function refreshAccess() {
    if (!user) return false;
    const access = await getAccess(user.uid);
    setBetaAccess(!!access.betaAccess);
    return !!access.betaAccess;
  }

  const value = useMemo(
    () => ({ user, betaAccess, loading, logout, refreshAccess }),
    [user, betaAccess, loading]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
